import { Box, CircularProgress, Grid2 } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { calculateCreatedAgo, printEventDates } from '../../../components/commonFunc';
import { isHubEventType } from '../helper/hubItemHref';
import { KC_COLORS } from '../../knowledge-center/theme';
import '../../knowledge-center/knowledgeCenter.css';
import { HubPagination } from './HubPagination';
import { ListingCardImage } from './ListingCardImage';
import { HubListingCardLink } from './HubListingCardLink';
import {
  excerptFromContent,
  eventLocationLine,
  getHubContentKind,
  parseHubListingTitle,
} from './parseHubListingTitle';

function HubGridCard({ itemData }) {
  const kind = getHubContentKind(itemData.postType);
  const { eyebrow, headline, fullTitle } = parseHubListingTitle(
    itemData.title,
    itemData.postType,
  );
  const isEvent = isHubEventType(itemData.postType);
  const excerpt =
    excerptFromContent(itemData.content, 120) ||
    (isEvent ? eventLocationLine(itemData) : '');
  const meta = isEvent ? printEventDates(itemData) : calculateCreatedAgo(itemData);

  return (
    <HubListingCardLink
      item={itemData}
      className="kc-featured-card group flex h-full w-full flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white no-underline text-inherit transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(10,29,58,0.12)]"
    >
      <div className="w-full overflow-hidden" style={{ backgroundColor: KC_COLORS.primaryNavy }}>
        <ListingCardImage
          mediaPath={itemData.postMedia}
          title={fullTitle}
          postType={itemData.postType}
          alt={fullTitle}
          showFallbackTitle={false}
          containerClassName="w-full aspect-[16/10]"
          imageClassName="w-full aspect-[16/10] object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
        />
      </div>
      <Box className="flex flex-1 flex-col p-4 sm:p-[1.05rem]" style={{ textAlign: 'left' }}>
        <p
          className="m-0 mb-2 text-[11px] font-bold uppercase tracking-[0.08em]"
          style={{ color: KC_COLORS.teal }}
        >
          {eyebrow}
        </p>
        <h3
          className="m-0 mb-2 line-clamp-3 text-[18px] font-semibold leading-[26px] transition-colors duration-200 group-hover:text-[#007a86]"
          style={{
            color: KC_COLORS.primaryNavy,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
          title={fullTitle}
        >
          {headline}
        </h3>
        {excerpt ? (
          <p
            className="m-0 mb-3 line-clamp-2 text-[14px] leading-[22px]"
            style={{ color: KC_COLORS.gray600 }}
          >
            {excerpt}
          </p>
        ) : null}
        <div
          className="mt-auto flex items-center justify-between gap-3 pt-2 text-[12px] leading-4"
          style={{ color: KC_COLORS.gray600 }}
        >
          <span className="min-w-0 line-clamp-1">
            {meta}
            {!isEvent ? (
              <>
                <span className="mx-1.5">·</span>
                Charlee.ai
              </>
            ) : null}
          </span>
          <span
            className="inline-flex shrink-0 items-center gap-0.5 font-semibold"
            style={{ color: KC_COLORS.teal }}
          >
            {kind.ctaShort}
            <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
          </span>
        </div>
      </Box>
    </HubListingCardLink>
  );
}

export function HubListingView({
  title,
  firstItem,
  renderFeatured,
  pageItems,
  loading,
  totalPages,
  currentPage,
  onPageChange,
  hasQuery = false,
  query = '',
  matchCount = 0,
}) {
  const showEmpty = !loading && hasQuery && matchCount === 0;
  const showGrid = !loading && pageItems.length > 0;
  const showPager = !loading && !showEmpty;

  return (
    <Box className="kc-ds hub-listing w-full text-start">
      <h1 className="kc-type-h2 my-4" style={{ color: KC_COLORS.primaryNavy }}>
        {title}
      </h1>
      {hasQuery ? (
        <p
          className="m-0 mb-4 text-[16px] leading-[24px]"
          style={{ color: KC_COLORS.gray600 }}
        >
          {matchCount === 0 ? (
            <>
              No results for{' '}
              <span className="font-semibold" style={{ color: KC_COLORS.primaryNavy }}>
                &apos;{query}&apos;
              </span>
            </>
          ) : (
            <>
              Showing{' '}
              <span className="font-semibold" style={{ color: KC_COLORS.primaryNavy }}>
                {matchCount}
              </span>{' '}
              {matchCount === 1 ? 'result' : 'results'} for{' '}
              <span className="font-semibold" style={{ color: KC_COLORS.primaryNavy }}>
                &apos;{query}&apos;
              </span>
            </>
          )}
        </p>
      ) : null}
      {firstItem && renderFeatured && !hasQuery ? (
        <Box className="w-full my-4">{renderFeatured(firstItem)}</Box>
      ) : null}
      {loading ? (
        <Box className="w-full flex justify-center items-center min-h-[220px] py-4">
          <CircularProgress sx={{ color: KC_COLORS.teal }} />
        </Box>
      ) : null}
      {showEmpty ? (
        <p className="py-12 text-center" style={{ color: KC_COLORS.gray600 }}>
          Try a different keyword, or clear search to browse this section.
        </p>
      ) : null}
      {showGrid ? (
        <Box className="w-full py-4">
          <Grid2 container spacing={2.5}>
            {pageItems.map((itemData, key) => (
              <Grid2
                key={`${itemData.postId ?? key}-${key}`}
                size={{ xs: 12, sm: 6, md: 4 }}
              >
                <HubGridCard itemData={itemData} />
              </Grid2>
            ))}
          </Grid2>
        </Box>
      ) : null}
      {showPager ? (
        <Box
          className="w-full py-6 md:py-8"
          sx={{ borderTop: `1px solid ${KC_COLORS.border}` }}
        >
          <HubPagination
            count={totalPages}
            page={currentPage}
            onChange={onPageChange}
          />
        </Box>
      ) : null}
    </Box>
  );
}
